/**
 * Build a normalized player ROI from COCO-style joints (same idea as iOS
 * PlayerRoiPoseDetector.boundingRect / backend roi_from_pose_joints).
 * Used after full-frame auto-detect to seed the tracked crop box.
 */

const DEFAULT_VISIBILITY = 0.2;
const DEFAULT_PADDING = 0.04;
const MIN_W = 0.04;
const MIN_H = 0.06;

function clamp01(n) {
  return Math.min(1, Math.max(0, n));
}

/**
 * @param {Array<{x?: number, y?: number, score?: number}|null>|null|undefined} joints
 * @param {{ visibility?: number, padding?: number }=} opts
 * @returns {{x:number,y:number,w:number,h:number}|null}
 */
export function roiFromJoints(joints, opts = {}) {
  if (!Array.isArray(joints) || joints.length < 5) return null;
  const visibility = opts.visibility ?? DEFAULT_VISIBILITY;
  const padding = opts.padding ?? DEFAULT_PADDING;

  let minX = 1;
  let minY = 1;
  let maxX = 0;
  let maxY = 0;
  let any = false;
  for (const j of joints) {
    if (!j) continue;
    if ((j.score ?? 1) < visibility) continue;
    const x = Number(j.x);
    const y = Number(j.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
    any = true;
  }
  if (!any) return null;

  const raw = {
    x: minX,
    y: minY,
    w: Math.max(MIN_W, maxX - minX),
    h: Math.max(MIN_H, maxY - minY),
  };
  return padNormalizedRoi(raw, padding);
}

/**
 * @param {{x:number,y:number,w:number,h:number}} roi
 * @param {number} pad
 */
export function padNormalizedRoi(roi, pad = DEFAULT_PADDING) {
  if (!roi) return null;
  const p = Math.max(0, Number(pad) || 0);
  const x = clamp01(roi.x - p);
  const y = clamp01(roi.y - p);
  const x2 = clamp01(roi.x + roi.w + p);
  const y2 = clamp01(roi.y + roi.h + p);
  return {
    x,
    y,
    w: Math.max(MIN_W, x2 - x),
    h: Math.max(MIN_H, y2 - y),
  };
}

/**
 * Map calibrate-frame suggest payload → left/right ROIs.
 * @param {{ suggested_left?: { joints?: unknown }, suggested_right?: { joints?: unknown } }|null|undefined} data
 */
export function roisFromSuggestedPlayers(data) {
  const left = roiFromJoints(data?.suggested_left?.joints);
  const right = roiFromJoints(data?.suggested_right?.joints);
  return { left, right };
}
